import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type { TAnalysisTestFocusForm } from '@/globals/typescript/utils/analysisTestFocusForm'
import type {
  IAnalysisTestMultiSelectNumber,
  IAnalysisTestAnswer,
  IOrderSampleAnalysisTestConfigSnapshot,
} from '@team-milva/lab-models/resources'
import type { ITextList } from '@team-milva/lab-models/resources'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import { t } from '@/locales'

// Both builders need the same two things to turn a `result-texts` id into a
// display string; the staged one additionally needs items typed into a picker
// but not yet committed, so a just-created label previews immediately.
export type TLabelResolutionContext = {
  locale: string
  resultTexts: ITextList | undefined
  pendingTexts?: { id: string; text: string }[]
}

function resolveLabel(
  textID: string | null,
  resultTexts: ITextList | undefined,
  locale: string,
  pendingTexts: { id: string; text: string }[],
  fallback: 'yes' | 'no'
): string {
  const defaultLabel = fallback === 'yes' ? t('term.yes') : t('term.no')
  if (!textID) return defaultLabel

  const pending = pendingTexts.find((x) => x.id === textID)
  if (pending) return pending.text

  const item = resultTexts?.items[textID]
  // A label whose ITextObj lacks the requested locale falls back per the
  // standard chain; an id pointing at nothing left resolves to the default.
  return item ? getTextObjValue(item.text, locale) || defaultLabel : defaultLabel
}

// The shape AnalysisResultFields.vue needs to render one AnalysisTest's result-entry
// UI — deliberately independent of both the live model class and the staged focus-mode
// form, so the same component can preview either one (see buildResultFieldsConfigFrom*
// below).
export type TAnalysisResultFieldsConfig =
  // The two labels arrive already resolved to display strings, at the *user's*
  // locale — this config drives in-app UI only. The report resolves the same
  // ids at the report's locale, which is frequently a different one.
  | { type: 'prove-based'; provedLabel: string; notProvedLabel: string }
  | {
      type: 'value-based'
      hasTooLowToMeasure: boolean
      hasTooHighToMeasure: boolean
      minLimit: number | null
      maxLimit: number | null
      unitID: string
    }
  | { type: 'single-select'; answers: IAnalysisTestAnswer[] }
  | {
      type: 'multi-select-text'
      reportDescriptionIDs: string[]
      // Never taken from the config snapshot — the suffix lists are deliberately
      // not frozen there (see buildAnalysisTestConfigSnapshot.ts). Empty means
      // no suffix dropdown renders at all.
      suffixReportDescriptionIDs: string[]
      answers: IAnalysisTestAnswer[]
      answerMode: 'none' | 'optional' | 'required'
    }
  | {
      type: 'multi-select-number'
      reportDescriptionIDs: string[]
      suffixReportDescriptionIDs: string[]
      unitID: string
      answerOverrides: IAnalysisTestMultiSelectNumber['answerOverrides']
      hasSumValue: boolean
    }

export function buildResultFieldsConfigFromAnalysisTest(
  test: AnalysisTest,
  { locale, resultTexts }: TLabelResolutionContext,
  // Frozen at the moment this test was assigned to the order/sample — preferred
  // over the live test's own fields whenever present, so editing the catalog
  // afterward can't change what the technician sees mid-entry. Absent on orders
  // created before this field existed, and always absent for `prove-based`
  // (see the Analysis Setup Stability plan's Phase 2/3).
  configSnapshot?: IOrderSampleAnalysisTestConfigSnapshot
): TAnalysisResultFieldsConfig {
  if (test.type === 'value-based') {
    return {
      type: 'value-based',
      hasTooLowToMeasure: configSnapshot?.hasTooLowToMeasure ?? test.hasTooLowToMeasure,
      hasTooHighToMeasure: configSnapshot?.hasTooHighToMeasure ?? test.hasTooHighToMeasure,
      minLimit: configSnapshot?.minLimit ?? test.minLimit,
      maxLimit: configSnapshot?.maxLimit ?? test.maxLimit,
      unitID: configSnapshot?.unitID ?? test.unitID,
    }
  }
  if (test.type === 'single-select') {
    return { type: 'single-select', answers: test.answers }
  }
  if (test.type === 'multi-select-text') {
    return {
      type: 'multi-select-text',
      reportDescriptionIDs: configSnapshot?.reportDescriptionIDs ?? test.reportDescriptionIDs,
      suffixReportDescriptionIDs: test.suffixReportDescriptionIDs ?? [],
      answers: test.answers,
      answerMode: test.options.answerMode ?? 'optional',
    }
  }
  if (test.type === 'multi-select-number') {
    return {
      type: 'multi-select-number',
      reportDescriptionIDs: configSnapshot?.reportDescriptionIDs ?? test.reportDescriptionIDs,
      suffixReportDescriptionIDs: test.suffixReportDescriptionIDs ?? [],
      unitID: configSnapshot?.unitID ?? test.unitID,
      answerOverrides: test.answerOverrides,
      hasSumValue: test.options.hasSumValue ?? false,
    }
  }
  return {
    type: 'prove-based',
    provedLabel: resolveLabel(test.provedTextID, resultTexts, locale, [], 'yes'),
    notProvedLabel: resolveLabel(test.notProvedTextID, resultTexts, locale, [], 'no'),
  }
}

export function buildResultFieldsConfigFromStagedForm(
  form: TAnalysisTestFocusForm,
  { locale, resultTexts, pendingTexts = [] }: TLabelResolutionContext
): TAnalysisResultFieldsConfig {
  if (form.type === 'value-based') {
    return {
      type: 'value-based',
      hasTooLowToMeasure: form.hasTooLowToMeasure,
      hasTooHighToMeasure: form.hasTooHighToMeasure,
      minLimit: form.minLimit,
      maxLimit: form.maxLimit,
      unitID: form.unitID,
    }
  }
  if (form.type === 'single-select') {
    return { type: 'single-select', answers: form.answers }
  }
  if (form.type === 'multi-select-text') {
    return {
      type: 'multi-select-text',
      reportDescriptionIDs: form.reportDescriptionIDs,
      suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
      answers: form.answers,
      answerMode: form.answerMode,
    }
  }
  if (form.type === 'multi-select-number') {
    return {
      type: 'multi-select-number',
      reportDescriptionIDs: form.reportDescriptionIDs,
      suffixReportDescriptionIDs: form.suffixReportDescriptionIDs,
      unitID: form.unitID,
      answerOverrides: form.answerOverrides,
      hasSumValue: form.hasSumValue,
    }
  }
  return {
    type: 'prove-based',
    provedLabel: resolveLabel(form.provedTextID, resultTexts, locale, pendingTexts, 'yes'),
    notProvedLabel: resolveLabel(
      form.notProvedTextID,
      resultTexts,
      locale,
      pendingTexts,
      'no'
    ),
  }
}

// A hash of only the fields that actually change AnalysisResultFields.vue's rendered
// shape — used to force the preview to remount when the staged config changes, without
// remounting on unrelated edits (e.g. the test's name).
export function getResultFieldsConfigKey(
  config: TAnalysisResultFieldsConfig
): string {
  // A label is text, not shape. Including it would remount the preview — and
  // wipe whatever the user typed into its sandbox input — every time a label
  // picker changed, or the user switched UI locale.
  if (config.type === 'prove-based') return JSON.stringify({ type: config.type })
  return JSON.stringify(config)
}
